import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, ExternalLink, Eye, PackagePlus, Pencil, XCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ListPagination } from '@/components/efs/ListPagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PageHeader } from '@/components/efs/PageHeader';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import { QueryFailureNotice } from '@/features/audit-issues/QueryFailureNotice';
import { InboundCreateDialog } from '@/features/operations/inbound/InboundCreateDialog';
import type { CreateInboundLotWithPackagesResponse } from '@/features/operations/inbound/types';

import { DispatchStatusBadge } from '../DispatchStatusBadge';
import { DispatchDateRangeFilter } from '../DispatchDateRangeFilter';
import { DispatchRequestPreviewSheet } from '../DispatchRequestPreviewSheet';
import { DispatchStopsSummary } from '../DispatchStopsSummary';
import { formatDispatchTime } from '../format';
import { RejectDispatchDialog } from '../RejectDispatchDialog';
import { AcceptDispatchDialog } from './AcceptDispatchDialog';
import { SupplierRequestDialog } from '../supplier/SupplierRequestDialog';
import { useSuppliersList } from '@/features/masters/suppliers/useSuppliers';
import {
  useDispatchRequests,
  useFacilities,
  useFacilityRequests,
  useRejectFacilityRequest,
} from '../useDispatch';
import {
  dispatchRequestStatusValues,
  type DispatchRequestDto,
  type DispatchRequestStatus,
} from '../types';

const NS = 'operations';
const ALL_FACILITIES = '__all__';
const DEFAULT_PER_PAGE = 50;
const PAGE_SIZE_OPTIONS = [50, 100];

/**
 * Facility staff view of routed dispatch requests for the current batch.
 * The route is `/batches/:batchId/dispatch`. Facility scope is resolved
 * from the authenticated staff scope. Company fallback only covers admins
 * that are not pinned to one facility.
 */
interface FacilityDispatchPageProps {
  embedded?: boolean;
}

export const FacilityDispatchPage = ({ embedded = false }: FacilityDispatchPageProps) => {
  const { t } = useTranslation(NS);
  const navigate = useNavigate();
  const user = useCurrentUser();
  const params = useParams<{ batchId?: string }>();
  const batchId = params.batchId;

  const facilities = useFacilities();
  const suppliersQuery = useSuppliersList({ active_only: false, per_page: 500 });
  const scopedFacilityId = useMemo(() => {
    if (user?.facilityId) return user.facilityId;
    const rows = facilities.data ?? [];
    if (user?.companyId) {
      return rows.find((facility) => facility.company_id === user.companyId)?.facility_id;
    }
    return undefined;
  }, [facilities.data, user?.companyId, user?.facilityId]);
  const canCrossFacilityScope =
    hasPermission(user, 'system.admin') || hasPermission(user, 'dispatch.request.route');
  const canChooseFacility = canCrossFacilityScope && !scopedFacilityId;
  const [selectedFacilityId, setSelectedFacilityId] = useState<string>(ALL_FACILITIES);
  const facilityId =
    scopedFacilityId ?? (selectedFacilityId === ALL_FACILITIES ? undefined : selectedFacilityId);
  const showAllFacilities = canChooseFacility && selectedFacilityId === ALL_FACILITIES;

  // 'all' means no server-side filter — show every status the facility
  // can see (routed, accepted, fulfilled, rejected). Default to 'all' so
  // a stale filter doesn't hide records when the operator switches batch.
  const [statusFilter, setStatusFilter] = useState<DispatchRequestStatus | 'all'>('all');
  const serverStatus = statusFilter === 'all' ? undefined : statusFilter;
  const [requestedDateFrom, setRequestedDateFrom] = useState('');
  const [requestedDateTo, setRequestedDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const listParams = {
    page,
    per_page: perPage,
    ...(serverStatus ? { status: serverStatus } : {}),
    ...(batchId ? { monthly_batch_id: batchId } : {}),
    ...(requestedDateFrom ? { requested_date_from: requestedDateFrom } : {}),
    ...(requestedDateTo ? { requested_date_to: requestedDateTo } : {}),
  };
  const facilityList = useFacilityRequests(facilityId, listParams);
  const allFacilityList = useDispatchRequests(listParams, showAllFacilities);
  const reject = useRejectFacilityRequest();

  const [acceptOpen, setAcceptOpen] = useState(false);
  const [target, setTarget] = useState<DispatchRequestDto | null>(null);
  const [rejectTarget, setRejectTarget] = useState<DispatchRequestDto | null>(null);
  const [inboundCreateTarget, setInboundCreateTarget] = useState<DispatchRequestDto | null>(null);
  const [previewRow, setPreviewRow] = useState<DispatchRequestDto | null>(null);
  const [editRow, setEditRow] = useState<DispatchRequestDto | null>(null);

  useEffect(() => {
    setPage(1);
  }, [batchId]);

  const rows = useMemo(() => {
    const source = showAllFacilities ? allFacilityList.data?.data : facilityList.data?.data;
    const all = (source ?? []).filter((row) => !showAllFacilities || row.routed_facility_id);
    return all;
  }, [allFacilityList.data, facilityList.data, showAllFacilities]);
  const supplierNameById = new Map(
    (suppliersQuery.data?.rows ?? []).map((supplier) => [
      supplier.company_id,
      supplier.display_name,
    ]),
  );
  const facilityNameById = new Map(
    (facilities.data ?? []).map((facility) => [facility.facility_id, facility.facility_name]),
  );
  const canAccept = hasPermission(user, 'dispatch.request.accept');
  const canEdit =
    user?.partyType === 'processor' &&
    !!user.facilityId &&
    hasPermission(user, 'dispatch.request.edit');
  const canReject = hasPermission(user, 'dispatch.request.reject_facility');
  const canFulfill = hasPermission(user, 'dispatch.request.fulfill');
  const activeList = showAllFacilities ? allFacilityList : facilityList;
  const meta = activeList.data?.meta;
  const total = meta?.total ?? rows.length;
  const columnCount = showAllFacilities ? 10 : 9;

  const openAccept = (row: DispatchRequestDto) => {
    setTarget(row);
    setAcceptOpen(true);
  };

  const onReject = (row: DispatchRequestDto) => {
    reject.reset();
    setRejectTarget(row);
  };

  const openInboundLot = (row: DispatchRequestDto) => {
    if (!row.monthly_batch_id || !row.linked_inbound_lot_id) return;
    navigate(`/batches/${row.monthly_batch_id}/inbound/${row.linked_inbound_lot_id}`);
  };

  const onInboundCreated = async (created: CreateInboundLotWithPackagesResponse) => {
    if (!inboundCreateTarget) return;
    await activeList.refetch();
    if (inboundCreateTarget.monthly_batch_id) {
      navigate(
        `/batches/${inboundCreateTarget.monthly_batch_id}/inbound/${created.inbound_lot_id}`,
      );
    }
  };

  const inboundDefaults = inboundCreateTarget
    ? {
        inbound_date:
          inboundCreateTarget.receivable_date ??
          inboundCreateTarget.requested_date ??
          new Date().toISOString().slice(0, 10),
        source_company_id: inboundCreateTarget.supplier_company_id,
        vehicle: inboundCreateTarget.vehicle_number ?? '',
        notes: inboundCreateTarget.notes ?? '',
      }
    : undefined;

  return (
    <div className="space-y-4">
      {!embedded ? (
        <PageHeader
          title={t('efs.operations.dispatch.title')}
          subtitle={t('efs.operations.dispatch.subtitle')}
          count={total}
        />
      ) : null}

      <div className="flex flex-wrap items-center gap-3 rounded-md border border-efs-border bg-efs-bg-panel p-3">
        {canChooseFacility ? (
          <div className="flex items-center gap-2">
            <label className="text-xs text-efs-text-muted">
              {t('efs.operations.dispatch.fields.facility', { defaultValue: '據點' })}
            </label>
            <Select
              value={selectedFacilityId}
              onValueChange={(value) => {
                setPage(1);
                setSelectedFacilityId(value);
              }}
            >
              <SelectTrigger className="h-8 w-64 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_FACILITIES}>
                  {t('efs.operations.dispatch.facility.all_facilities')}
                </SelectItem>
                {(facilities.data ?? []).map((facility) => (
                  <SelectItem key={facility.facility_id} value={facility.facility_id}>
                    {facility.facility_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
        <div className="flex items-center gap-2">
          <label className="text-xs text-efs-text-muted">
            {t('efs.operations.dispatch.fields.status', { defaultValue: '狀態' })}
          </label>
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setPage(1);
              setStatusFilter(v as DispatchRequestStatus | 'all');
            }}
          >
            <SelectTrigger className="h-8 w-40 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t('efs.operations.dispatch.statuses.all', { defaultValue: '全部' })}
              </SelectItem>
              {dispatchRequestStatusValues.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`efs.operations.dispatch.statuses.${s}`, { defaultValue: s })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <DispatchDateRangeFilter
        from={requestedDateFrom}
        to={requestedDateTo}
        onFromChange={(value) => {
          setPage(1);
          setRequestedDateFrom(value);
        }}
        onToChange={(value) => {
          setPage(1);
          setRequestedDateTo(value);
        }}
        onClear={() => {
          setPage(1);
          setRequestedDateFrom('');
          setRequestedDateTo('');
        }}
      />

      <div className="rounded-md border border-efs-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('efs.operations.dispatch.fields.type')}</TableHead>
              <TableHead>{t('efs.operations.dispatch.fields.supplier')}</TableHead>
              <TableHead>{t('efs.operations.dispatch.fields.trip_code')}</TableHead>
              <TableHead>{t('efs.operations.dispatch.fields.requested_date')}</TableHead>
              <TableHead>
                {t('efs.operations.dispatch.fields.requested_time', {
                  defaultValue: '希望時間',
                })}
              </TableHead>
              <TableHead>{t('efs.operations.dispatch.fields.location')}</TableHead>
              <TableHead>{t('efs.operations.dispatch.fields.notes')}</TableHead>
              {showAllFacilities ? (
                <TableHead>
                  {t('efs.operations.dispatch.fields.facility', { defaultValue: '據點' })}
                </TableHead>
              ) : null}
              <TableHead>
                {t('efs.operations.dispatch.fields.status', { defaultValue: '狀態' })}
              </TableHead>
              <TableHead className="text-right">
                {t('efs.operations.dispatch.fields.actions', { defaultValue: '操作' })}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!facilityId && !showAllFacilities ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="py-6 text-center text-efs-text-muted">
                  {t('efs.operations.dispatch.facility.no_facility', {
                    defaultValue: '尚未取得設施資訊',
                  })}
                </TableCell>
              </TableRow>
            ) : activeList.isLoading ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="py-6 text-center text-efs-text-muted">
                  {t('efs.common.messages.loading', { ns: 'common' })}
                </TableCell>
              </TableRow>
            ) : activeList.isError ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="p-4">
                  <QueryFailureNotice
                    error={activeList.error}
                    onRetry={() => void activeList.refetch()}
                  />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="py-6 text-center text-efs-text-muted">
                  {t('efs.common.messages.empty', { ns: 'common' })}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.dispatch_request_id}>
                  <TableCell className="text-xs">
                    {t(`efs.operations.dispatch.types.${row.request_type}`, {
                      defaultValue: row.request_type,
                    })}
                  </TableCell>
                  <TableCell className="text-xs">
                    {supplierNameById.get(row.supplier_company_id) ??
                      `${row.supplier_company_id.slice(0, 8)}…`}
                  </TableCell>
                  <TableCell className="text-xs">
                    <span className="font-mono text-efs-text-muted">
                      {row.trip_code ?? row.temporary_code}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs">{row.requested_date ?? '—'}</TableCell>
                  <TableCell className="text-xs">
                    {formatDispatchTime(row.requested_time)}
                  </TableCell>
                  <TableCell className="text-xs">
                    <DispatchStopsSummary row={row} />
                  </TableCell>
                  <TableCell className="max-w-[18rem] whitespace-pre-wrap text-xs">
                    {row.notes?.trim() ? row.notes : '—'}
                  </TableCell>
                  {showAllFacilities ? (
                    <TableCell className="text-xs">
                      {row.routed_facility_id
                        ? (facilityNameById.get(row.routed_facility_id) ??
                          `${row.routed_facility_id.slice(0, 8)}…`)
                        : '—'}
                    </TableCell>
                  ) : null}
                  <TableCell>
                    <div className="space-y-1">
                      <DispatchStatusBadge status={row.status} />
                      {row.status === 'accepted' || row.status === 'fulfilled' ? (
                        <p
                          className={
                            'text-[11px] ' +
                            (row.linked_inbound_lot_id
                              ? 'text-emerald-900 dark:text-emerald-100'
                              : 'text-amber-900 dark:text-amber-100')
                          }
                        >
                          {row.linked_inbound_lot_id
                            ? t('efs.operations.dispatch.facility.inbound_linked', {
                                defaultValue: '入荷ロット作成済み',
                              })
                            : t('efs.operations.dispatch.facility.inbound_unlinked', {
                                defaultValue: '入荷ロット未作成',
                              })}
                        </p>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="space-x-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setPreviewRow(row)}>
                      <Eye className="mr-1 h-3 w-3" aria-hidden="true" />
                      {t('efs.operations.dispatch.actions.preview', {
                        defaultValue: '檢視',
                      })}
                    </Button>
                    {canEdit &&
                    row.routed_facility_id === user?.facilityId &&
                    (row.status === 'routed' || row.status === 'accepted') ? (
                      <Button size="sm" variant="outline" onClick={() => setEditRow(row)}>
                        <Pencil className="mr-1 h-3 w-3" />
                        修改
                      </Button>
                    ) : null}
                    {row.status === 'routed' && canAccept ? (
                      <Button size="sm" variant="default" onClick={() => openAccept(row)}>
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                        {t('efs.operations.dispatch.actions.accept')}
                      </Button>
                    ) : null}
                    {(row.status === 'routed' || row.status === 'accepted') && canReject ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={reject.isPending}
                        onClick={() => onReject(row)}
                      >
                        <XCircle className="mr-1 h-3 w-3" />
                        {t('efs.operations.dispatch.actions.reject')}
                      </Button>
                    ) : null}
                    {row.linked_inbound_lot_id && canFulfill ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!row.monthly_batch_id}
                        onClick={() => openInboundLot(row)}
                      >
                        <ExternalLink className="mr-1 h-3 w-3" />
                        {t('efs.operations.dispatch.actions.open_inbound_lot', {
                          defaultValue: '開啟入荷ロット',
                        })}
                      </Button>
                    ) : null}
                    {row.status === 'accepted' && !row.linked_inbound_lot_id && canFulfill ? (
                      <Button
                        size="sm"
                        variant="default"
                        disabled={!row.monthly_batch_id}
                        onClick={() => setInboundCreateTarget(row)}
                      >
                        <PackagePlus className="mr-1 h-3 w-3" />
                        {t('efs.operations.dispatch.actions.create_inbound_lot', {
                          defaultValue: '入荷ロット作成',
                        })}
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <ListPagination
        page={meta?.page ?? page}
        perPage={meta?.per_page ?? perPage}
        total={total}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={setPage}
        onPerPageChange={(nextPerPage) => {
          setPage(1);
          setPerPage(nextPerPage === 100 ? 100 : DEFAULT_PER_PAGE);
        }}
      />

      <AcceptDispatchDialog open={acceptOpen} onOpenChange={setAcceptOpen} request={target} />
      <SupplierRequestDialog
        key={editRow?.dispatch_request_id ?? 'processor-edit'}
        open={!!editRow}
        onOpenChange={(open) => {
          if (!open) setEditRow(null);
        }}
        row={editRow}
        processorEdit
      />
      <DispatchRequestPreviewSheet
        open={!!previewRow}
        onOpenChange={(open) => {
          if (!open) setPreviewRow(null);
        }}
        request={previewRow}
        supplierName={previewRow ? supplierNameById.get(previewRow.supplier_company_id) : undefined}
        facilityName={
          previewRow?.routed_facility_id
            ? facilityNameById.get(previewRow.routed_facility_id)
            : undefined
        }
      />
      <RejectDispatchDialog
        open={!!rejectTarget}
        onOpenChange={(open) => {
          if (!open) setRejectTarget(null);
        }}
        request={rejectTarget}
        isPending={reject.isPending}
        error={reject.error}
        onConfirm={(body) => {
          if (!rejectTarget) return;
          reject.mutate(
            { id: rejectTarget.dispatch_request_id, body },
            { onSuccess: () => setRejectTarget(null) },
          );
        }}
      />
      {inboundCreateTarget?.monthly_batch_id ? (
        <InboundCreateDialog
          batchId={inboundCreateTarget.monthly_batch_id}
          open={!!inboundCreateTarget}
          onOpenChange={(open) => {
            if (!open) setInboundCreateTarget(null);
          }}
          dispatchRequestId={inboundCreateTarget.dispatch_request_id}
          defaults={inboundDefaults}
          navigateOnCreated={false}
          onCreated={onInboundCreated}
        />
      ) : null}
    </div>
  );
};

export default FacilityDispatchPage;
